import React from "react"
import type { ReactNode } from "react"

/**
 * Recursively extracts text content from a JSX tree.
 *
 * @example
 * extractText(<Button>Upgrade <strong>now</strong></Button>) // "Upgrade now"
 *
 * With `skipAriaHidden`, `aria-hidden` subtrees are ignored so the result
 * matches what assistive tech announces. Use it when a duplicated or decorative
 * visible label is hidden from AT via `aria-hidden` (e.g. a responsive label
 * paired with a `VisuallyHidden` one) and would otherwise be double-counted.
 */
export function extractText(
  node: ReactNode,
  { skipAriaHidden = false }: { skipAriaHidden?: boolean } = {}
): string {
  if ([null, undefined, true, false].includes(node as null)) {
    return ""
  }

  const parts: (string | number)[] = []

  React.Children.forEach(node, child => {
    if (typeof child === "string" || typeof child === "number") {
      parts.push(child)
    } else if (React.isValidElement(child)) {
      if (skipAriaHidden && isAriaHidden(child)) {
        return
      }
      const text = extractText(
        (child.props as { children?: ReactNode }).children,
        { skipAriaHidden }
      )
      if (text) parts.push(text)
    }
  })

  const text = parts.join(" ").replace(/\s+/g, " ")
  // Only trim on the `skipAriaHidden` path, where a skipped leading node can
  // leave a stray space. Leaving the default path untrimmed keeps every
  // existing caller's emitted telemetry byte-for-byte unchanged.
  return skipAriaHidden ? text.trim() : text
}

function isAriaHidden(element: React.ReactElement): boolean {
  const { "aria-hidden": value } = element.props as React.AriaAttributes
  return value === true || value === "true"
}
